import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'

import Button from '../components/Button'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import SortableTableHead from '../components/SortableTableHead'
import ButtonRunDownload from '../components/ButtonRunDownload'
import GridCircularProgress from '../components/GridCircularProgress'
import DialogDeleteAffiliazione from '../components/DialogDeleteAffiliazione'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { selectAffiliazioniSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetAffiliazioniQuery,
  useLazyGetAffiliazioneByIdQuery,
  useDeleteAffiliazioneMutation,
  useDeleteIscrizioniBySocietaMutation,
} from '../hooks/api-slice'
import { setAffiliazione } from '../container/affiliazioni'

const columnsAffiliazioni = [
  { label: 'Codice', sortable: true, sortingField: 'codiceSocieta' },
  { label: 'Ragione sociale', sortable: false },
  { label: 'Nome breve', sortable: true, sortingField: 'nomeSocieta' },
  { label: 'Zona', sortable: true, sortingField: 'zona' },
  { label: 'Stato', sortable: true, sortingField: 'status' },
  { label: '', sortable: false },
]

const Affiliazioni = () => {
  const { isLoading } = useGetAffiliazioniQuery()
  const affiliazioni = useSelector(selectAffiliazioniSorted)

  const [triggerGetAffiliazioneById] = useLazyGetAffiliazioneByIdQuery()
  const [deleteAffiliazioneMutation] = useDeleteAffiliazioneMutation()
  const [deleteIscrizioniBySocietaMutation] =
    useDeleteIscrizioniBySocietaMutation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { setFeedback } = useFeedback()

  document.title = `${config?.name} - Società`

  // state to manage the delete dialog
  const [openDeleteAffiliazione, setOpenDeleteAffiliazione] = useState()
  const [deletingAffiliazione, setDeletingAffiliazione] = useState()

  const handleClickOpenDeleteAffiliazione = a => {
    setDeletingAffiliazione(a)
    setOpenDeleteAffiliazione(true)
  }

  const handleCloseDeleteAffiliazione = () => {
    setOpenDeleteAffiliazione()
    setDeletingAffiliazione()
  }

  const editAffiliazione = async id => {
    const result = await triggerGetAffiliazioneById(id)
    if (!result?.error) {
      dispatch(setAffiliazione(result?.data))
      navigate('/affiliazione')
    }
  }

  const deleteAffiliazione = async (affiliazione, deleteAlsoIscrizioni) => {
    if (deleteAlsoIscrizioni) {
      const result = await deleteIscrizioniBySocietaMutation(
        affiliazione?.codiceSocieta,
      )
      if (result?.error) {
        setFeedback("Errore durante l'eliminazione delle iscrizioni.")
        return
      }
    }

    const result = await deleteAffiliazioneMutation(affiliazione?._id)
    if (!result?.error) {
      setFeedback('Società eliminata correttamente.', false)
      handleCloseDeleteAffiliazione()
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Società' />
      <Grid container sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <center>
            <Button
              to='/affiliazione'
              onClick={() => dispatch(setAffiliazione())}
              children='Nuova società'
            />
          </center>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ButtonRunDownload
            buttonText='Scarica Excel'
            algorithm='14_GenerateExcelAffiliazioni'
            type='XLSX'
            disabled={!affiliazioni?.length}
          />
        </Grid>
      </Grid>
      {(isLoading && <GridCircularProgress />) ||
        (!affiliazioni && (
          <GridHeader text='Elenco delle società non trovato.' />
        )) ||
        (affiliazioni.length === 0 && (
          <GridHeader text='Non è ancora stata inserita nessuna società.' />
        )) || (
          <Grid container sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead
                  table={affiliazioni}
                  columns={columnsAffiliazioni}
                  tableName='affiliazioni'
                />
                <TableBody>
                  {affiliazioni.map((a, index) => {
                    return (
                      <TableRow
                        key={index}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: colours.white,
                          ':hover': { backgroundColor: colours.greyVeryLight },
                        }}
                        onClick={() => editAffiliazione(a.id)}
                      >
                        <TableCell>{a.codiceSocieta}</TableCell>
                        <TableCell>{a.denominazioneSocieta}</TableCell>
                        <TableCell>{a.nomeSocieta}</TableCell>
                        <TableCell>{a.zona}</TableCell>
                        <TableCell>
                          <Chip
                            variant={
                              a.status === 'complete' || !a.isActive
                                ? 'outlined'
                                : 'filled'
                            }
                            label={
                              (!a.isActive && 'inattiva') ||
                              (a.status === 'complete' && 'completa') ||
                              (a.status === 'incomplete' && 'da completare') ||
                              'sconosciuto'
                            }
                            color={
                              (!a.isActive && 'error') ||
                              (a.status === 'complete' && 'success') ||
                              (a.status === 'incomplete' && 'warning') ||
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <GridIconButton
                            type='delete'
                            onClick={e => {
                              e.stopPropagation()
                              handleClickOpenDeleteAffiliazione(a)
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <DialogDeleteAffiliazione
        open={openDeleteAffiliazione}
        handleClose={handleCloseDeleteAffiliazione}
        triggerFunction={deleteAffiliazione}
        affiliazione={deletingAffiliazione}
      />
    </Grid>
  )
}

export default Affiliazioni
